/**
 * ModeCanvasMount — Framer-Motion FLIP wrapper for mode-switch animation.
 *
 * Cycle 101 sprint-3 Task 3.5 (sprint.md:309).
 *
 * Contract:
 *   * Wraps `ModuleCanvas` in a `motion.div` keyed by `mode`. Mode-switch
 *     triggers `AnimatePresence` exit → entrance, so canvas fades + slides
 *     (<250ms p95 target per master §Performance NFRs Step 1).
 *   * Respects `prefers-reduced-motion` (WCAG 2.2 + sprint-3 AC-9 + SDD §5.4):
 *     animation collapses to an instant swap — no transition duration.
 *
 * The hook (`useModeCanvasLayout`) is NOT called here — it lives in the V2
 * shell so the same layout instance can feed `ModuleCanvas` (via this
 * wrapper), `ModeIdentityStrip` (for module-count readout), `+ FAB`
 * (Task 3.6, add-placement), and `RESET PRESET` (Task 3.7, resetToPreset).
 * Two hook instances would produce competing writes to the same
 * localStorage key in the same render.
 */

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ModuleCanvas } from './ModuleCanvas';
import type { UseModeCanvasLayoutResult } from '../../hooks/useModeCanvasLayout';
import type { ModeId } from '../../lib/modePresets';

export interface ModeCanvasMountProps {
  readonly mode: ModeId;
  readonly layout: UseModeCanvasLayoutResult;
  /** Container id — ModesRow points aria-controls at this (sprint-2 AC-8). */
  readonly canvasId?: string;
  readonly onPlacementFocus?: (placementId: string) => void;
}

/**
 * Motion variants for mode-switch FLIP. Budget <250ms p95; we target well
 * below that so there's headroom on low-end devices. Perf-harness-verified
 * in sprint-3 / sprint-5 infrastructure.
 */
const canvasVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

export function ModeCanvasMount({
  mode,
  layout,
  canvasId,
  onPlacementFocus,
}: ModeCanvasMountProps) {
  const reducedMotion = useReducedMotion();

  const transition = reducedMotion
    ? { duration: 0 }
    : { duration: 0.14, ease: 'easeOut' as const };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={mode}
        variants={canvasVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={transition}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1,
        }}
      >
        <ModuleCanvas
          layout={layout}
          id={canvasId}
          onPlacementFocus={onPlacementFocus}
        />
      </motion.div>
    </AnimatePresence>
  );
}
